// src/pages/ProductsPage.js
import React, { useState, useEffect } from 'react';
import {
  Container,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Snackbar,
  Alert,
  Box,
  Typography,
  TablePagination,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  LinearProgress,
  CircularProgress,
} from '@mui/material';
import { Add, Edit, Delete } from '@mui/icons-material';
import { useProducts, useProduct, useProductMutations, useLeafCategories, uploadFile } from '../services/api';
import { useCategories } from '../services/api';
import { useTrademarks } from '../services/api';
import { resolveImageUrl } from '../utils/imageUrl';
import { useNavigate } from 'react-router-dom';

const ProductsPage = () => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [filters, setFilters] = useState();
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    nameArabic: '',
    description: '',
    imageUrl: '',
    price: '',
    cost:'',
    discount: '0',
    trademarkId: '',
    categoryId: '',
    packagePrice: '0',
    packageCount: '0',
    countity: '0',
    maximumByOrder: '100',
    isActive: true,
  });
  const [selectedFile, setSelectedFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);

  const { data, isLoading } = useProducts({
    page: page + 1,
    per_page: rowsPerPage,
    ...filters,
  });
  
  const { products = [], meta = {} } = data || {};
  const { data: categories } = useCategories();
  const { data: leafCategories = [] } = useLeafCategories();

  const { data: trademarks } = useTrademarks();
  const { data: product } = useProduct(editingId);
  const { createMutation, updateMutation, deleteMutation } = useProductMutations();

  const navigate = useNavigate(); // Make sure this is at the top with other hooks

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedFile(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setIsUploading(true);
      
      if (!selectedFile && !formData.imageUrl) {
        throw new Error('Please upload an image');
      }

      const data = {
        ...formData,
        price: parseFloat(formData.price) || 0,
        cost: parseFloat(formData.cost) || 0,
        discount: parseFloat(formData.discount) || 0,
        packagePrice: parseFloat(formData.packagePrice) || 0,
        packageCount: parseInt(formData.packageCount, 10) || 0,
        countity: parseInt(formData.countity, 10) || 0,
        maximumByOrder: parseInt(formData.maximumByOrder, 10) || 100,
      };

      // Upload file if selected
      if (selectedFile) {
        const uploadResponse = await uploadFile(selectedFile);
        if (uploadResponse.status) {
          data.imageUrl = uploadResponse.data.url;
        } else {
          throw new Error(uploadResponse.message || 'Failed to upload image');
        }
      }
      
      if (data.imageUrl && !data.imageUrl.startsWith('http')) {
        data.imageUrl = 'http://' + data.imageUrl;
      }

      if (editingId) {
        await updateMutation.mutateAsync({ id: editingId, ...data });
        showSnackbar('Product updated successfully');
        handleClose();
        navigate('/products');
      } else {
        await createMutation.mutateAsync(data);
        showSnackbar('Product created successfully');
        handleClose();
        navigate('/products');
      }
    } catch (error) {
      const message = error.response?.data?.message || 'An error occurred';
      showSnackbar(message, 'error');
    }
  };

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  });

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({
      [name]: value,
    }));
    setPage(0);
  };

  const handleOpen = (id = null) => {
    // Reset file input when opening the dialog
    setSelectedFile(null);
    if (id) {
      setEditingId(id);
    } else {
      setFormData({
        name: '',
        nameArabic: '',
        description: '',
        imageUrl: '',
        price: '',
        cost: '',
        discount: '0',
        trademarkId: '',
        categoryId: '',
        packagePrice: '0',
        packageCount: '0',
        countity: '0',
        maximumByOrder: '100',
        isActive: true,
      });
      setEditingId(null);
    }
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setEditingId(null);
    setSelectedFile(null);
    setIsUploading(false);
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this product?')) {
      try {
        await deleteMutation.mutateAsync(id);
        showSnackbar('Product deleted successfully');
      } catch (error) {
        showSnackbar('Failed to delete product', 'error');
      }
    }
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  // Update form data when product data is loaded for editing
  useEffect(() => {
    if (product) {
      setFormData({
        name: product.name || '',
        nameArabic: product.nameArabic || '',
        description: product.description || '',
        imageUrl: product.imageUrl || '',
        price: product.price?.toString() || '0',
        cost: product.cost?.toString() || '0',
        discount: product.discount?.toString() || '0',
        trademarkId: product.trademark?.id || '',
        categoryId: product.category?.id || '',
        packagePrice: product.packagePrice?.toString() || '0',
        packageCount: product.packageCount?.toString() || '0',
        countity: product.countity?.toString() || '0',
        maximumByOrder: product.maximumByOrder?.toString() || '100',
        isActive: product.isActive ?? true,
      });
    }
  }, [product]);

  if (isLoading && !data) {
    return <LinearProgress />;
  }

  return (
    <Container>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h4">Products</Typography>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => handleOpen()}
        >
          Add Product
        </Button>
      </Box>

      <Box sx={{ mb: 3, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
        <FormControl sx={{ minWidth: 200 }} size="small">
          <InputLabel>Category</InputLabel>
          <Select
            name="category_id"
            value={filters?.category_id || ""}
            onChange={handleFilterChange}
            label="Category"
            required
          >
            <MenuItem value="">All Categories</MenuItem>
            {leafCategories.map((category) => (
              <MenuItem key={category.id} value={category.id}>
                {category.nameArabic}
              </MenuItem>
            ))}
          </Select>
          <FormHelperText>Only leaf categories (without children) can be selected</FormHelperText>
        </FormControl>

        <FormControl sx={{ minWidth: 200 }} size="small">
          <InputLabel>Trademark</InputLabel>
          <Select
            name="trademark_id"
            value={filters?.trademark_id || ""}
            onChange={handleFilterChange}
            label="Trademark"
          >
            <MenuItem value="">All Trademarks</MenuItem>
            {trademarks?.map((trademark) => (
              <MenuItem key={trademark.id} value={trademark.id}>
                {trademark.nameArabic}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Arabic Name</TableCell>
              <TableCell>Price</TableCell>
              <TableCell>Cost</TableCell>
              <TableCell>Stock</TableCell>
              <TableCell>Category</TableCell>
              <TableCell>Trademark</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  <LinearProgress />
                </TableCell>
              </TableRow>
            ) : products?.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  No products found
                </TableCell>
              </TableRow>
            ) : (
              products?.map((product) => (
                <TableRow key={product.id}>
                  <TableCell>{product.name}</TableCell>
                  <TableCell>{product.nameArabic}</TableCell>
                  <TableCell>${parseFloat(product.price).toFixed(2)}</TableCell>
                  <TableCell>${parseFloat(product.cost).toFixed(2)}</TableCell>
                  <TableCell>{product.countity}</TableCell>
                  <TableCell>{product.category?.name || 'N/A'}</TableCell>
                  <TableCell>{product.trademark?.name || 'N/A'}</TableCell>
                  <TableCell>
                    {product.isActive ? (
                      <Typography color="success.main">Active</Typography>
                    ) : (
                      <Typography color="error">Inactive</Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <IconButton onClick={() => handleOpen(product.id)} size="small">
                      <Edit fontSize="small" />
                    </IconButton>
                    <IconButton 
                      onClick={() => handleDelete(product.id)} 
                      size="small"
                      color="error"
                    >
                      <Delete fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={meta.total || 0}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </TableContainer>

      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
        <form onSubmit={handleSubmit}>
          <DialogTitle>
            {editingId ? 'Edit Product' : 'Add New Product'}
          </DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mt: 1 }}>
              <TextField
                margin="dense"
                name="name"
                label="Name (English)"
                fullWidth
                required
                value={formData.name}
                onChange={handleChange}
              />
              <TextField
                margin="dense"
                name="nameArabic"
                label="Name (Arabic)"
                fullWidth
                required
                value={formData.nameArabic}
                onChange={handleChange}
              />
              <TextField
                margin="dense"
                name="description"
                label="Description"
                fullWidth
                multiline
                rows={2}
                value={formData.description}
                onChange={handleChange}
              />
              <Box>
                <input
                  accept="image/*"
                  style={{ display: 'none' }}
                  id="image-upload"
                  type="file"
                  onChange={handleFileChange}
                  required={!editingId}
                />
                <label htmlFor="image-upload">
                  <Button 
                    variant="outlined" 
                    component="span" 
                    fullWidth 
                    sx={{ mt: 1 }}
                    startIcon={<Add />}
                  >
                    {selectedFile ? 'Change Image' : 'Upload Image'}
                  </Button>
                </label>
                {selectedFile ? (
                <Box mt={1}>
                  <Typography variant="caption" display="block">Selected image:</Typography>
                  <Box 
                    component="img" 
                    src={URL.createObjectURL(selectedFile)} 
                    alt="Selected" 
                    sx={{ maxWidth: '100%', maxHeight: 150, mt: 1, borderRadius: 1 }}
                  />
                </Box>
                ) : formData.imageUrl ? (
                  <Box mt={1}>
                    <Typography variant="caption" display="block">Current image:</Typography>
                    <Box 
                      component="img" 
                      src={resolveImageUrl(formData.imageUrl)} 
                      alt="Current" 
                      sx={{ maxWidth: '100%', maxHeight: 150, mt: 1, borderRadius: 1 }}
                    />
                  </Box>
                ) : null}
              </Box>
              <TextField
                margin="dense"
                name="price"
                label="Price"
                type="number"
                fullWidth
                required
                inputProps={{ step: "0.01", min: "0" }}
                value={formData.price}
                onChange={handleChange}
              />
              <TextField
                margin="dense"
                name="cost"
                label="Cost"
                type="number"
                fullWidth
                required
                inputProps={{ step: "0.01", min: "0" }}
                value={formData.cost}
                onChange={handleChange}
              />
              <TextField
                margin="dense"
                name="discount"
                label="Discount"
                type="number"
                fullWidth
                inputProps={{ step: "1", min: "0", max: "100" }}
                value={formData.discount}
                onChange={handleChange}
              />
              <FormControl fullWidth margin="dense" required>
                <InputLabel>Category</InputLabel>
                <Select
                  name="categoryId"
                  value={formData.categoryId}
                  onChange={handleChange}
                  label="Category"
                  required
                >
                  {leafCategories.map((category) => (
                    <MenuItem key={category.id} value={category.id}>
                      {category.nameArabic}
                    </MenuItem>
                  ))}
                </Select>
                <FormHelperText>Only leaf categories (without children) can be selected</FormHelperText>
              </FormControl>
              <FormControl fullWidth margin="dense" required>
                <InputLabel>Trademark</InputLabel>
                <Select
                  name="trademarkId"
                  value={formData.trademarkId}
                  onChange={handleChange}
                  label="Trademark"
                  required
                >
                  {trademarks?.map((trademark) => (
                    <MenuItem key={trademark.id} value={trademark.id}>
                      {trademark.nameArabic}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                margin="dense"
                name="packagePrice"
                label="Package Price"
                type="number"
                fullWidth
                inputProps={{ step: "0.01", min: "0" }}
                value={formData.packagePrice}
                onChange={handleChange}
              />
              <TextField
                margin="dense"
                name="packageCount"
                label="Items per Package"
                type="number"
                fullWidth
                inputProps={{ min: "0" }}
                value={formData.packageCount}
                onChange={handleChange}
              />
              <TextField
                margin="dense"
                name="countity"
                label="Quantity in Stock"
                type="number"
                fullWidth
                inputProps={{ min: "0" }}
                value={formData.countity}
                onChange={handleChange}
              />
              <TextField
                margin="dense"
                name="maximumByOrder"
                label="Maximum Per Order"
                type="number"
                fullWidth
                inputProps={{ min: "1" }}
                value={formData.maximumByOrder}
                onChange={handleChange}
              />
              <FormControl fullWidth margin="dense">
                <InputLabel>Status</InputLabel>
                <Select
                  name="isActive"
                  value={formData.isActive}
                  onChange={handleChange}
                  label="Status"
                >
                  <MenuItem value={true}>Active</MenuItem>
                  <MenuItem value={false}>Inactive</MenuItem>
                </Select>
              </FormControl>
            </Box>
          </DialogContent>
          <DialogActions>
            {isUploading && <CircularProgress size={24} sx={{ mr: 2 }} />}
            <Button onClick={handleClose}>Cancel</Button>
            <Button type="submit" variant="contained">
              {editingId ? 'Update' : 'Create'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default ProductsPage;